import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MessageCircle, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACCESS_TYPES = ["Driveway", "Garage", "Commercial / Site", "Other"];

const inputCls =
    "w-full bg-coal border border-ash px-4 py-4 text-paper placeholder:text-grit/60 font-mono text-sm focus:outline-none focus:border-amber transition-colors duration-200 rounded-none";

export default function QuoteForm() {
    const [form, setForm] = useState({ name: "", phone: "", postcode: "", email: "", access_type: "Driveway", message: "" });
    const [status, setStatus] = useState("idle");

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setStatus("sending");
        try {
            const payload = { ...form, access_type: form.access_type.toLowerCase() };
            if (!payload.email) delete payload.email;
            await axios.post(`${API}/quotes`, payload);
            setStatus("sent");
            toast.success("Quote request received — we'll call you back within one working day.");
        } catch (err) {
            setStatus("idle");
            toast.error("Something went wrong sending that. Please call or WhatsApp us instead.");
        }
    };

    return (
        <section id="quote" className="relative bg-tarmac py-24 sm:py-32 grain border-t border-ash" data-testid="quote-section">
            <div className="px-5 sm:px-8 max-w-7xl mx-auto relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-16">
                <div>
                    <motion.p
                        className="font-mono text-xs tracking-[0.25em] uppercase text-amber mb-4"
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.6 }}
                    >
                        [ Free, fixed-price quote ]
                    </motion.p>
                    <motion.h2
                        className="font-display uppercase leading-[0.9] tracking-tight text-4xl sm:text-5xl lg:text-7xl mb-8"
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7 }}
                    >
                        Drop us<br />a line.
                    </motion.h2>
                    <motion.p
                        className="text-base lg:text-lg text-grit leading-relaxed max-w-md mb-10"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                    >
                        Tell us where you need smooth access. We'll survey it, price it
                        fixed, and handle the council from day one.
                    </motion.p>

                    <motion.div
                        className="flex flex-col gap-3 max-w-md"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7, delay: 0.15 }}
                    >
                        <a
                            data-testid="quote-call-btn"
                            href="tel:+447971124650"
                            className="flex items-center justify-between border border-ash px-6 py-5 hover:border-amber transition-colors duration-200 group"
                        >
                            <span className="flex items-center gap-3 font-mono text-sm tracking-[0.15em] uppercase">
                                <Phone size={16} className="text-amber" /> 07971 124 650
                            </span>
                            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-grit group-hover:text-amber transition-colors duration-200">Mon–Sat 8–6</span>
                        </a>
                        <a
                            data-testid="quote-whatsapp-btn"
                            href="https://wa.me/447971124650?text=Hi%20Drop%20Kerbs%20UK%2C%20I%27d%20like%20a%20quote%20for%20a%20dropped%20kerb."
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-between border border-ash px-6 py-5 hover:border-amber transition-colors duration-200 group"
                        >
                            <span className="flex items-center gap-3 font-mono text-sm tracking-[0.15em] uppercase">
                                <MessageCircle size={16} className="text-amber" /> WhatsApp us
                            </span>
                            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-grit group-hover:text-amber transition-colors duration-200">Photos welcome</span>
                        </a>
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, delay: 0.1 }}
                >
                    {status === "sent" ? (
                        <div className="border border-amber p-10 sm:p-14 text-center" data-testid="quote-success-panel">
                            <CheckCircle2 size={48} className="text-amber mx-auto mb-6" />
                            <h3 className="font-display uppercase text-3xl mb-4">Request received.</h3>
                            <p className="text-grit leading-relaxed mb-8">
                                We'll call you back within one working day to arrange your free survey.
                            </p>
                            <button
                                data-testid="quote-send-another-btn"
                                onClick={() => { setForm({ name: "", phone: "", postcode: "", email: "", access_type: "Driveway", message: "" }); setStatus("idle"); }}
                                className="px-8 py-4 border border-ash font-mono text-xs tracking-[0.2em] uppercase hover:border-amber hover:text-amber transition-colors duration-200"
                            >
                                Send another
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="border border-ash p-6 sm:p-10 bg-tarmac/60" data-testid="quote-form">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label htmlFor="q-name" className="block font-mono text-[10px] tracking-[0.2em] uppercase text-grit mb-2">Name *</label>
                                    <input id="q-name" data-testid="quote-name-input" required value={form.name} onChange={set("name")} placeholder="Jane Smith" className={inputCls} />
                                </div>
                                <div>
                                    <label htmlFor="q-phone" className="block font-mono text-[10px] tracking-[0.2em] uppercase text-grit mb-2">Phone *</label>
                                    <input id="q-phone" data-testid="quote-phone-input" required type="tel" value={form.phone} onChange={set("phone")} placeholder="07700 900123" className={inputCls} />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label htmlFor="q-postcode" className="block font-mono text-[10px] tracking-[0.2em] uppercase text-grit mb-2">Postcode *</label>
                                    <input id="q-postcode" data-testid="quote-postcode-input" required value={form.postcode} onChange={set("postcode")} placeholder="M1 2AB" className={inputCls} />
                                </div>
                                <div>
                                    <label htmlFor="q-access" className="block font-mono text-[10px] tracking-[0.2em] uppercase text-grit mb-2">Access for</label>
                                    <select id="q-access" data-testid="quote-access-select" value={form.access_type} onChange={set("access_type")} className={inputCls}>
                                        {ACCESS_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="mb-4">
                                <label htmlFor="q-email" className="block font-mono text-[10px] tracking-[0.2em] uppercase text-grit mb-2">Email (get instant confirmation)</label>
                                <input id="q-email" data-testid="quote-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} />
                            </div>
                            <div className="mb-6">
                                <label htmlFor="q-message" className="block font-mono text-[10px] tracking-[0.2em] uppercase text-grit mb-2">Anything else</label>
                                <textarea id="q-message" data-testid="quote-message-input" rows={4} value={form.message} onChange={set("message")} placeholder="e.g. Standard pavement, two cars wide, lamppost nearby…" className={`${inputCls} resize-none`} />
                            </div>
                            <button
                                type="submit"
                                data-testid="quote-submit-btn"
                                disabled={status === "sending"}
                                className="w-full flex items-center justify-center gap-3 bg-amber text-tarmac font-mono text-sm tracking-[0.2em] uppercase font-bold py-5 hover:bg-paper disabled:opacity-60 transition-colors duration-200"
                            >
                                {status === "sending" ? (
                                    <><Loader2 size={16} className="animate-spin" /> Sending…</>
                                ) : (
                                    "Get my free quote"
                                )}
                            </button>
                            <p className="mt-4 font-mono text-[10px] tracking-[0.15em] uppercase text-grit text-center">
                                Council approved · Fully insured · Fixed price
                            </p>
                        </form>
                    )}
                </motion.div>
            </div>
        </section>
    );
}
